import React, { useEffect, useState } from "react";
import styles from "./student-promotion.module.css";
import { Result, Spin, Typography } from "antd";
import CustomTable from "../../../common/custom-table/custom-table";
import { fetchPromotionLevels } from "../../../services/student_promotion";
import { toast } from "react-toastify";
import CustomButton from "../../../common/custom-button/custom-button";
import ManagePromotionDrawer from "./components/edit-promotion-drawer/manage-promotion-drawer";
import { LockOutlined } from "@ant-design/icons";
import usePromotionPolling from "../../../hooks/usePromotionPolling";

const StudentPromotion = () => {
  const [promotionData, setPromotionData] = useState({});
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [promotionEnabled, setPromotionEnabled] = useState(false);
  const { startPolling, stopPolling } = usePromotionPolling({
    onComplete: () => getPromotionLevels(),
  });

  useEffect(() => {
    getPromotionLevels();
    startPolling();
    return () => {
      stopPolling();
    };
  }, []);

  const getPromotionLevels = async () => {
    try {
      setLoading(true);
      const response = await fetchPromotionLevels();
      if (response?.data) {
        setPromotionData(response?.data?.data);
      }
      setPromotionEnabled(true);
      setLoading(false);
    } catch (err) {
      if (err?.status === 403) {
        setPromotionEnabled(false);
      } else {
        toast.error(err?.data?.message || "Failed to fetch promotion levels");
      }
      console.log(err);
      setLoading(false);
    }
  };

  const onViewDetails = (record) => {
    setSelectedPromotion(record);
    setOpenDrawer(true);
  };

  const handleOnClose = () => {
    getPromotionLevels();
    setOpenDrawer(false);
  };

  const columns = [
    {
      title: "Level",
      dataIndex: "current_level",
      key: "current_level",
      sorter: (a, b) => a?.current_level?.name?.localeCompare(b?.current_level?.name),
      render: (value) => value?.name || "-",
    },
    {
      title: "Promote To",
      dataIndex: "next_level",
      key: "next_level",
      sorter: (a, b) => a?.next_level?.name?.localeCompare(b?.next_level?.name),
      render: (value) => value?.name || "-",
      align: "center",
    },
    {
      title: "Current Students",
      dataIndex: "current_level",
      key: "current_level",
      sorter: (a, b) => a?.current_level?.total_students - b?.current_level?.total_students,
      render: (value) => value?.total_students || "0",
      align: "center",
    },
    {
      title: "Promoted Students",
      dataIndex: "next_level",
      key: "next_level",
      sorter: (a, b) => a?.next_level?.total_students - b?.next_level?.total_students,
      render: (value) => value?.total_students || "0",
      align: "center",
    },
    {
      title: "Action",
      render: (_, record) => (
        <div className={styles.viewDetailsWrapper}>
          <CustomButton
            title="Promote Students"
            disabled={!record?.current_level?.total_students || !record?.next_level}
            onClick={() => onViewDetails(record)}
          />
        </div>
      ),
      align: "center",
    },
  ];

  if (loading) {
    return (
      <div className={styles.loadingWrapper}>
        <Spin size="large" />
      </div>
    );
  } else if (!promotionEnabled) {
    return (
      <Result
        icon={<LockOutlined />}
        title="Promotion Management Temporarily Unavailable"
        subTitle="To maintain academic data integrity, student promotions are restricted and can only be managed at the end of the academic year."
        className={styles.blockedResult}
      />
    );
  };

  return (
    <div>
      <div className={styles.titleContainer}>
        <Typography className={styles.tabTitle}>
          Student Promotion
        </Typography>
      </div>
      <p>{`Academic year: ${promotionData?.currentAcademicYear ?? ""}`}</p>

      <div className={styles.tableContainer}>
        <CustomTable
          columns={columns}
          dataSource={promotionData?.levels ?? []}
          loading={loading}
          headerColor="#667085"
          headerBackgroundColor="#F9FAFB"
          pagination={false}
        />
      </div>
      <ManagePromotionDrawer 
        open={openDrawer} 
        onClose={handleOnClose}
        record={selectedPromotion}
      />
    </div>
  );
};

export default StudentPromotion;
